import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Package, Plus, Trash2, Edit, X, ShieldCheck, Tag, FileText, ChevronDown, ChevronUp, Wand2, ScanLine, Printer, Copy,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const CART_STATUS = [
    { value: "active", label: "Operacional" },
    { value: "in_service", label: "Em manutenção" },
    { value: "retired", label: "Inactivo" },
    { value: "lost", label: "Perdido / Roubado" },
];

const CART_MODELS = [
    { value: "alfa", label: "Alfa" },
    { value: "omega", label: "Ômega" },
    { value: "custom", label: "Personalizado" },
];

const emptyCart = () => ({
    user_id: "",
    model: "alfa",
    serial_number: "",
    commercial_model: "",
    technical_code: "",
    production_year: new Date().getFullYear(),
    nickname: "",
    purchase_date: "",
    warranty_months: 24,
    maintenance_interval_months: 12,
    notes: "",
    status: "active",
    assigned_parts: [],
});

const emptyPart = (category = null) => ({
    id: "",  // filled by backend
    name: "",
    sku: "",
    category,
    quantity: 1,
    unit_price_eur: 0,
    warranty_months: null,
    supplier: "",
    notes: "",
    installed_at: "",
    // Unit-identity extensions
    catalog_id: "",
    manufacturer: "",
    technical_model: "",
    reference: "",
    component_serial: "",
    purchase_date: "",
    documentation_url: "",
    manual_url: "",
    state: "active",
});

export default function AdminEquipments() {
    const [items, setItems] = useState([]);
    const [users, setUsers] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);  // cart id or "new"
    const [form, setForm] = useState(emptyCart());
    const [duplicating, setDuplicating] = useState(null); // cart object being duplicated
    const [dupForm, setDupForm] = useState({ user_id: "", serial_number: "", nickname: "", purchase_date: "" });

    async function load() {
        setLoading(true);
        try {
            const [carts, us, cats] = await Promise.all([
                api.get("/admin/carts"),
                api.get("/admin/users"),
                api.get("/admin/cart-part-categories"),
            ]);
            setItems(carts.data || []);
            setUsers(us.data || []);
            setCategories(cats.data || []);
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Erro ao carregar.");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { load(); }, []);

    function openCreate() {
        setEditing("new");
        setForm(emptyCart());
    }

    function openEdit(eq) {
        setEditing(eq.id);
        setForm({
            user_id: eq.user_id || "",
            model: eq.model || "alfa",
            serial_number: eq.serial_number || "",
            commercial_model: eq.commercial_model || "",
            technical_code: eq.technical_code || "",
            production_year: eq.production_year || new Date().getFullYear(),
            nickname: eq.nickname || "",
            purchase_date: eq.purchase_date || "",
            warranty_months: eq.warranty_months ?? 24,
            maintenance_interval_months: eq.maintenance_interval_months ?? 12,
            notes: eq.notes || "",
            status: eq.status || "active",
            assigned_parts: (eq.assigned_parts || []).map((p) => ({ ...p })),
        });
    }

    function closeEditor() {
        setEditing(null);
        setForm(emptyCart());
    }

    function onField(k, v) {
        setForm((f) => ({ ...f, [k]: v }));
    }

    // ------- Parts management -------
    function addPart(category) {
        setForm((f) => ({ ...f, assigned_parts: [...f.assigned_parts, emptyPart(category)] }));
    }
    function updatePart(idx, patch) {
        setForm((f) => ({
            ...f,
            assigned_parts: f.assigned_parts.map((p, i) => (i === idx ? { ...p, ...patch } : p)),
        }));
    }
    function removePart(idx) {
        setForm((f) => ({ ...f, assigned_parts: f.assigned_parts.filter((_, i) => i !== idx) }));
    }

    // ------- Save / delete cart -------
    async function save() {
        // Basic validation
        if (editing === "new" && !form.user_id) return toast.error("Seleccione um cliente.");
        // Serial is optional on new (backend auto-generates); on update it must remain valid
        if (editing !== "new" && (!form.serial_number || form.serial_number.trim().length < 3)) {
            return toast.error("Nº de série inválido.");
        }
        for (const p of form.assigned_parts) {
            if (!p.name || !p.name.trim()) return toast.error("Cada peça tem de ter um nome.");
        }

        // Prepare payload — coerce numbers, nulls
        const cleanParts = form.assigned_parts.map((p) => {
            const q = {
                ...p,
                name: p.name.trim(),
                sku: (p.sku || "").trim() || null,
                supplier: (p.supplier || "").trim() || null,
                notes: (p.notes || "").trim() || null,
                installed_at: p.installed_at || null,
                quantity: Number(p.quantity) || 1,
                unit_price_eur: p.unit_price_eur === "" || p.unit_price_eur === null ? null : Number(p.unit_price_eur),
                warranty_months: p.warranty_months === "" || p.warranty_months === null || p.warranty_months === undefined
                    ? null
                    : Number(p.warranty_months),
                // unit-identity extension fields
                catalog_id: (p.catalog_id || "").trim() || null,
                manufacturer: (p.manufacturer || "").trim() || null,
                technical_model: (p.technical_model || "").trim() || null,
                reference: (p.reference || "").trim() || null,
                component_serial: (p.component_serial || "").trim() || null,
                purchase_date: p.purchase_date || null,
                documentation_url: (p.documentation_url || "").trim() || null,
                manual_url: (p.manual_url || "").trim() || null,
                state: p.state || "active",
            };
            // strip empty id so backend generates one
            if (!q.id) delete q.id;
            return q;
        });

        const payload = {
            model: form.model,
            serial_number: form.serial_number ? form.serial_number.trim() : null,
            commercial_model: (form.commercial_model || "").trim() || null,
            technical_code: (form.technical_code || "").trim() || null,
            production_year: form.production_year ? Number(form.production_year) : null,
            nickname: (form.nickname || "").trim() || null,
            purchase_date: form.purchase_date || null,
            warranty_months: Number(form.warranty_months) || 24,
            maintenance_interval_months: Number(form.maintenance_interval_months) || 12,
            notes: (form.notes || "").trim() || null,
            status: form.status,
            assigned_parts: cleanParts,
        };
        if (editing === "new") payload.user_id = form.user_id;

        try {
            if (editing === "new") {
                const { data } = await api.post(`/admin/carts`, payload);
                toast.success(`Carrinho criado com nº ${data?.serial_number || ""}`);
            } else {
                await api.put(`/admin/carts/${editing}`, payload);
                toast.success("Carrinho actualizado.");
            }
            closeEditor();
            load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Erro ao guardar.");
        }
    }

    async function del(eq) {
        if (!window.confirm(`Eliminar o carrinho ${eq.serial_number}?`)) return;
        try {
            await api.delete(`/admin/carts/${eq.id}`);
            toast.success("Eliminado.");
            load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Erro ao eliminar.");
        }
    }

    function openDuplicate(eq) {
        setDuplicating(eq);
        setDupForm({
            user_id: eq.user_id || "",
            serial_number: "",
            nickname: eq.nickname ? `${eq.nickname} (cópia)` : "",
            purchase_date: new Date().toISOString().slice(0, 10),
        });
    }
    function closeDuplicate() {
        setDuplicating(null);
    }
    async function submitDuplicate(e) {
        e.preventDefault();
        if (!dupForm.user_id) return toast.error("Seleccione um cliente.");
        if (!dupForm.serial_number || dupForm.serial_number.trim().length < 3) return toast.error("Nº de série inválido.");
        try {
            await api.post(`/admin/carts/${duplicating.id}/duplicate`, {
                user_id: dupForm.user_id,
                serial_number: dupForm.serial_number.trim(),
                nickname: dupForm.nickname.trim() || null,
                purchase_date: dupForm.purchase_date || null,
            });
            toast.success("Carrinho duplicado com sucesso.");
            closeDuplicate();
            load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Erro ao duplicar.");
        }
    }

    async function applyGlobalWarranty() {
        if (editing === "new") {
            // apply locally — set all parts.warranty_months to null so they inherit
            setForm((f) => ({
                ...f,
                assigned_parts: f.assigned_parts.map((p) => ({ ...p, warranty_months: null })),
            }));
            toast.success("Aplicado localmente. Guarde para persistir.");
            return;
        }
        try {
            await api.post(`/admin/carts/${editing}/apply-global-warranty`);
            toast.success("Garantia global aplicada a todas as peças.");
            // refresh form
            const { data } = await api.get(`/admin/carts/${editing}`);
            openEdit(data.equipment);
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Erro.");
        }
    }

    function userLabel(uid) {
        const u = users.find((x) => x.id === uid);
        return u ? (u.name || u.email) : uid;
    }

    // ------- Live invoice preview (client-side calc) -------
    const preview = useMemo(() => {
        const catOrder = categories.map((c) => c.value);
        const bucket = Object.fromEntries(catOrder.map((c) => [c, []]));
        const nocat = [];
        let grand = 0;
        for (const p of form.assigned_parts) {
            const qty = Number(p.quantity) || 1;
            const up = Number(p.unit_price_eur) || 0;
            const total = qty * up;
            grand += total;
            const row = { ...p, line_total: total };
            if (p.category && bucket[p.category]) bucket[p.category].push(row);
            else nocat.push(row);
        }
        const sections = catOrder
            .filter((c) => bucket[c].length > 0)
            .map((c) => {
                const label = categories.find((x) => x.value === c)?.label || c;
                return {
                    category: c,
                    label,
                    items: bucket[c],
                    subtotal: bucket[c].reduce((s, it) => s + it.line_total, 0),
                };
            });
        if (nocat.length) {
            sections.push({
                category: null,
                label: "Sem categoria",
                items: nocat,
                subtotal: nocat.reduce((s, it) => s + it.line_total, 0),
            });
        }
        return { sections, grand };
    }, [form.assigned_parts, categories]);

    // ================================================================
    // LIST VIEW
    // ================================================================
    if (!editing) {
        return (
            <div data-testid="admin-equipments-page">
                <div className="flex items-end justify-between gap-4 flex-wrap">
                    <div>
                        <h2 className="font-heading text-2xl font-extrabold text-primary flex items-center gap-2">
                            <Package className="h-6 w-6" /> Carrinhos & Garantias
                        </h2>
                        <p className="text-sm text-muted-foreground">
                            Construa carrinhos por categoria (estrutura, cobertura, equipamento…) e gere folhas de requisitos e etiquetas.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Link to="/admin/label-plans"
                            data-testid="btn-label-plans"
                            className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2.5 text-sm font-bold text-primary hover:bg-muted">
                            <ScanLine className="h-4 w-4" /> Planos de etiquetas
                        </Link>
                        <button onClick={openCreate}
                            data-testid="equipments-new"
                            className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-bold hover:-translate-y-0.5 transition">
                            <Plus className="h-4 w-4" /> Novo carrinho
                        </button>
                    </div>
                </div>

                <div className="mt-6 rounded-2xl border border-border bg-white overflow-x-auto">
                    {loading ? (
                        <div className="p-8 text-sm text-muted-foreground">A carregar...</div>
                    ) : items.length === 0 ? (
                        <div className="p-8 text-sm text-muted-foreground text-center">Sem carrinhos registados.</div>
                    ) : (
                        <table className="w-full text-sm" data-testid="equipments-table">
                            <thead className="bg-muted text-[11px] uppercase tracking-wider text-muted-foreground">
                                <tr>
                                    <th className="text-left px-4 py-3">Cliente</th>
                                    <th className="text-left px-4 py-3">Modelo</th>
                                    <th className="text-left px-4 py-3">Nº série</th>
                                    <th className="text-left px-4 py-3">Peças</th>
                                    <th className="text-left px-4 py-3">Garantia até</th>
                                    <th className="text-left px-4 py-3">Estado</th>
                                    <th className="text-right px-4 py-3">Acções</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {items.map((eq) => (
                                    <tr key={eq.id} data-testid={`equip-row-${eq.id}`}>
                                        <td className="px-4 py-3 font-bold text-primary">{eq.owner?.name || eq.owner?.email || userLabel(eq.user_id)}</td>
                                        <td className="px-4 py-3 capitalize">{eq.model}</td>
                                        <td className="px-4 py-3 font-mono text-xs">{eq.serial_number}</td>
                                        <td className="px-4 py-3 text-xs">{(eq.assigned_parts || []).length}</td>
                                        <td className="px-4 py-3 text-xs">{eq.warranty_until || "—"}</td>
                                        <td className="px-4 py-3 capitalize text-xs">{eq.status}</td>
                                        <td className="px-4 py-3 text-right whitespace-nowrap">
                                            <Link to={`/admin/carts/${eq.id}/requirements`} target="_blank" rel="noreferrer"
                                                data-testid={`equip-requirements-${eq.id}`}
                                                className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-muted">
                                                <FileText className="h-3 w-3" /> Requisitos
                                            </Link>
                                            <button onClick={() => openEdit(eq)} data-testid={`equip-edit-${eq.id}`}
                                                className="ml-2 inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-muted">
                                                <Edit className="h-3 w-3" /> Editar
                                            </button>
                                            <button onClick={() => openDuplicate(eq)} data-testid={`equip-duplicate-${eq.id}`}
                                                className="ml-2 inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-muted">
                                                <Copy className="h-3 w-3" /> Duplicar
                                            </button>
                                            <button onClick={() => del(eq)} data-testid={`equip-delete-${eq.id}`}
                                                className="ml-2 inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700 hover:bg-red-100">
                                                <Trash2 className="h-3 w-3" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {duplicating && (
                    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={closeDuplicate}>
                        <form onSubmit={submitDuplicate} onClick={(e) => e.stopPropagation()}
                            data-testid="duplicate-form"
                            className="w-full max-w-lg rounded-2xl bg-white p-6 max-h-[90vh] overflow-y-auto">
                            <div className="flex items-center justify-between mb-3">
                                <div>
                                    <h3 className="font-heading text-xl font-extrabold text-primary flex items-center gap-2">
                                        <Copy className="h-5 w-5" /> Duplicar carrinho
                                    </h3>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Clonar <span className="font-mono">{duplicating.serial_number}</span> com {(duplicating.assigned_parts || []).length} peças. Serão gerados novos códigos únicos.
                                    </p>
                                </div>
                                <button type="button" onClick={closeDuplicate}><X className="h-5 w-5" /></button>
                            </div>
                            <div className="grid gap-3 mt-4">
                                <Field label="Cliente destinatário" required>
                                    <select value={dupForm.user_id} onChange={(e) => setDupForm((f) => ({ ...f, user_id: e.target.value }))} required
                                        data-testid="dup-user"
                                        className="w-full rounded-md border border-border bg-muted px-3 py-2.5 text-sm outline-none focus:border-primary focus:bg-white">
                                        <option value="">— Seleccione —</option>
                                        {users.filter((u) => u.role !== "admin").map((u) => (
                                            <option key={u.id} value={u.id}>{u.name || u.email} ({u.email})</option>
                                        ))}
                                    </select>
                                </Field>
                                <Field label="Novo nº de série" required>
                                    <input value={dupForm.serial_number} onChange={(e) => setDupForm((f) => ({ ...f, serial_number: e.target.value }))} required
                                        data-testid="dup-serial"
                                        placeholder="Único no sistema"
                                        className="w-full rounded-md border border-border bg-muted px-3 py-2.5 text-sm font-mono outline-none focus:border-primary focus:bg-white" />
                                </Field>
                                <Field label="Alcunha (opcional)">
                                    <input value={dupForm.nickname} onChange={(e) => setDupForm((f) => ({ ...f, nickname: e.target.value }))}
                                        className="w-full rounded-md border border-border bg-muted px-3 py-2.5 text-sm outline-none focus:border-primary focus:bg-white" />
                                </Field>
                                <Field label="Data de compra">
                                    <input type="date" value={dupForm.purchase_date} onChange={(e) => setDupForm((f) => ({ ...f, purchase_date: e.target.value }))}
                                        className="w-full rounded-md border border-border bg-muted px-3 py-2.5 text-sm outline-none focus:border-primary focus:bg-white" />
                                </Field>
                            </div>
                            <div className="mt-5 flex justify-end gap-2">
                                <button type="button" onClick={closeDuplicate}
                                    className="rounded-md border border-border px-4 py-2 text-sm font-bold">Cancelar</button>
                                <button type="submit" data-testid="dup-submit"
                                    className="rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-bold hover:-translate-y-0.5 transition">
                                    Duplicar carrinho
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        );
    }

    // ================================================================
    // EDITOR VIEW — cart builder
    // ================================================================
    return (
        <div data-testid="cart-builder">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <button onClick={closeEditor} className="text-xs uppercase tracking-wider font-bold text-primary/60 hover:text-primary inline-flex items-center gap-1">
                        ← Voltar aos carrinhos
                    </button>
                    <h2 className="mt-2 font-heading text-2xl font-extrabold text-primary flex items-center gap-2">
                        <Package className="h-6 w-6" />
                        {editing === "new" ? "Novo carrinho" : `Carrinho ${form.serial_number || ""}`}
                    </h2>
                </div>
                <div className="flex items-center gap-2">
                    {editing !== "new" && (
                        <Link to={`/admin/carts/${editing}/requirements`} target="_blank" rel="noreferrer"
                            data-testid="btn-open-requirements"
                            className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-xs font-bold text-primary hover:bg-muted">
                            <FileText className="h-3.5 w-3.5" /> Folha de requisitos
                        </Link>
                    )}
                    <button onClick={save} data-testid="btn-save-cart"
                        className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm font-bold hover:-translate-y-0.5 transition">
                        {editing === "new" ? "Criar carrinho" : "Guardar alterações"}
                    </button>
                </div>
            </div>

            <div className="mt-6 grid lg:grid-cols-3 gap-6">
                {/* LEFT: Cart info + parts builder (2 cols) */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Basic info */}
                    <section className="rounded-2xl border border-border bg-white p-5" data-testid="cart-basic-info">
                        <h3 className="font-heading font-black text-primary text-sm uppercase tracking-wider">Dados do carrinho</h3>
                        <div className="mt-4 grid gap-4 sm:grid-cols-2">
                            <Field label="Cliente" required>
                                <select value={form.user_id} disabled={editing !== "new"} onChange={(e) => onField("user_id", e.target.value)}
                                    data-testid="form-user"
                                    className="w-full rounded-md border border-border bg-muted px-3 py-2.5 text-sm outline-none focus:border-primary focus:bg-white disabled:opacity-60">
                                    <option value="">— Seleccione —</option>
                                    {users.filter((u) => u.role !== "admin").map((u) => (
                                        <option key={u.id} value={u.id}>{u.name || u.email} ({u.email})</option>
                                    ))}
                                </select>
                            </Field>
                            <Field label="Modelo" required>
                                <select value={form.model} onChange={(e) => onField("model", e.target.value)}
                                    data-testid="form-model"
                                    className="w-full rounded-md border border-border bg-muted px-3 py-2.5 text-sm outline-none focus:border-primary focus:bg-white">
                                    {CART_MODELS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                                </select>
                            </Field>
                            <Field label="Nº de série" required={editing !== "new"}>
                                <div className="flex gap-2">
                                    <input value={form.serial_number} onChange={(e) => onField("serial_number", e.target.value)}
                                        data-testid="form-serial"
                                        placeholder={editing === "new" ? "auto: NF-YY-XXXXXX" : ""}
                                        className="flex-1 rounded-md border border-border bg-muted px-3 py-2.5 text-sm font-mono outline-none focus:border-primary focus:bg-white" />
                                    {editing === "new" && (
                                        <button type="button"
                                            onClick={async () => {
                                                try {
                                                    const { data } = await api.post(`/admin/carts/generate-serial`, null, {
                                                        params: form.production_year ? { year: form.production_year } : {},
                                                    });
                                                    onField("serial_number", data.serial_number);
                                                    toast.success(`Nº atribuído: ${data.serial_number}`);
                                                } catch (err) {
                                                    toast.error(formatApiError(err?.response?.data?.detail) || "Erro a gerar nº");
                                                }
                                            }}
                                            className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-xs font-bold text-white hover:bg-primary/90 whitespace-nowrap"
                                            title="Gerar próximo nº sequencial (NF-YY-XXXXXX)"
                                            data-testid="form-generate-serial"
                                        >
                                            Gerar nº
                                        </button>
                                    )}
                                </div>
                                <p className="mt-1 text-[10px] text-muted-foreground">
                                    Formato NF-YY-XXXXXX. Deixe vazio para geração automática ao criar.
                                </p>
                            </Field>
                            <Field label="Modelo comercial">
                                <input value={form.commercial_model} onChange={(e) => onField("commercial_model", e.target.value)}
                                    data-testid="form-commercial-model"
                                    placeholder="Ex: NOVIFOOD Urban 120"
                                    className="w-full rounded-md border border-border bg-muted px-3 py-2.5 text-sm outline-none focus:border-primary focus:bg-white" />
                            </Field>
                            <Field label="Código técnico">
                                <input value={form.technical_code} onChange={(e) => onField("technical_code", e.target.value)}
                                    data-testid="form-technical-code"
                                    placeholder="Ex: NVC-120"
                                    className="w-full rounded-md border border-border bg-muted px-3 py-2.5 text-sm font-mono outline-none focus:border-primary focus:bg-white" />
                            </Field>
                            <Field label="Ano de fabrico">
                                <input type="number" min="2020" max="2099" value={form.production_year}
                                    onChange={(e) => onField("production_year", Number(e.target.value))}
                                    data-testid="form-production-year"
                                    className="w-full rounded-md border border-border bg-muted px-3 py-2.5 text-sm outline-none focus:border-primary focus:bg-white" />
                            </Field>
                            <Field label="Alcunha (opcional)">
                                <input value={form.nickname} onChange={(e) => onField("nickname", e.target.value)}
                                    data-testid="form-nickname"
                                    className="w-full rounded-md border border-border bg-muted px-3 py-2.5 text-sm outline-none focus:border-primary focus:bg-white" placeholder="Ex: Carrinho 1" />
                            </Field>
                            <Field label="Data de compra">
                                <input type="date" value={form.purchase_date} onChange={(e) => onField("purchase_date", e.target.value)}
                                    data-testid="form-purchase"
                                    className="w-full rounded-md border border-border bg-muted px-3 py-2.5 text-sm outline-none focus:border-primary focus:bg-white" />
                            </Field>
                            <Field label="Estado">
                                <select value={form.status} onChange={(e) => onField("status", e.target.value)}
                                    data-testid="form-status"
                                    className="w-full rounded-md border border-border bg-muted px-3 py-2.5 text-sm outline-none focus:border-primary focus:bg-white">
                                    {CART_STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                                </select>
                            </Field>
                            <Field label="Garantia global (meses)">
                                <input type="number" min={0} max={240} value={form.warranty_months}
                                    onChange={(e) => onField("warranty_months", e.target.value)}
                                    data-testid="form-warranty-months"
                                    className="w-full rounded-md border border-border bg-muted px-3 py-2.5 text-sm outline-none focus:border-primary focus:bg-white" />
                            </Field>
                            <Field label="Manutenção (meses)">
                                <input type="number" min={1} max={60} value={form.maintenance_interval_months}
                                    onChange={(e) => onField("maintenance_interval_months", e.target.value)}
                                    data-testid="form-maintenance-months"
                                    className="w-full rounded-md border border-border bg-muted px-3 py-2.5 text-sm outline-none focus:border-primary focus:bg-white" />
                            </Field>
                            <div className="sm:col-span-2">
                                <Field label="Notas internas">
                                    <textarea rows={2} value={form.notes} onChange={(e) => onField("notes", e.target.value)}
                                        className="w-full rounded-md border border-border bg-muted px-3 py-2.5 text-sm outline-none focus:border-primary focus:bg-white" />
                                </Field>
                            </div>
                        </div>

                        <div className="mt-4 flex flex-wrap gap-2 items-center">
                            <button onClick={applyGlobalWarranty}
                                data-testid="btn-apply-global-warranty"
                                className="inline-flex items-center gap-2 rounded-full border border-secondary bg-secondary/10 text-primary px-3 py-1.5 text-xs font-bold hover:bg-secondary/20">
                                <Wand2 className="h-3.5 w-3.5" /> Aplicar garantia global a todas as peças
                            </button>
                            <span className="text-[11px] text-muted-foreground">
                                (Limpa a garantia individual das peças — todas passam a herdar {form.warranty_months || 24}m)
                            </span>
                        </div>
                    </section>

                    {/* Parts by category */}
                    <section className="space-y-3" data-testid="parts-sections">
                        <div className="flex items-center justify-between">
                            <h3 className="font-heading font-black text-primary text-sm uppercase tracking-wider">
                                Peças por categoria ({form.assigned_parts.length})
                            </h3>
                        </div>
                        {categories.map((cat) => {
                            const idxs = form.assigned_parts
                                .map((p, i) => ({ p, i }))
                                .filter(({ p }) => p.category === cat.value);
                            return (
                                <CategorySection
                                    key={cat.value}
                                    category={cat}
                                    entries={idxs}
                                    onAdd={() => addPart(cat.value)}
                                    onChange={updatePart}
                                    onRemove={removePart}
                                    globalWarrantyMonths={form.warranty_months}
                                    editing={editing}
                                />
                            );
                        })}
                    </section>
                </div>

                {/* RIGHT: live invoice preview */}
                <aside className="lg:sticky lg:top-24 self-start rounded-2xl border border-border bg-white p-5" data-testid="invoice-preview">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-primary">
                            <FileText className="h-4 w-4" />
                            <span className="text-xs font-bold uppercase tracking-[0.16em]">Pré-visualização</span>
                        </div>
                        {editing !== "new" && (
                            <Link to={`/admin/carts/${editing}/requirements`} target="_blank" rel="noreferrer"
                                className="text-[10px] uppercase tracking-wider font-bold text-primary hover:underline">
                                Abrir folha
                            </Link>
                        )}
                    </div>
                    <p className="mt-1 text-[11px] text-muted-foreground">
                        Lista de requisitos que será associada a este modelo.
                    </p>

                    <div className="mt-4 space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                        {preview.sections.length === 0 && (
                            <div className="text-xs text-muted-foreground italic">Ainda sem peças. Adicione acima ↑</div>
                        )}
                        {preview.sections.map((sec) => (
                            <div key={sec.category || "none"} className="border-b border-border pb-2">
                                <div className="flex items-baseline justify-between text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                                    <span>{sec.label}</span>
                                    <span>{sec.subtotal.toFixed(2)} €</span>
                                </div>
                                <ul className="mt-1 space-y-0.5">
                                    {sec.items.map((it, i) => (
                                        <li key={i} className="flex items-baseline justify-between text-xs">
                                            <span className="truncate flex-1 pr-2">
                                                <span className="font-bold text-primary">{it.quantity}×</span>{" "}
                                                {it.name || <em className="text-muted-foreground">(sem nome)</em>}
                                            </span>
                                            <span className="tabular-nums text-primary">{(it.line_total || 0).toFixed(2)} €</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    <div className="mt-4 border-t-2 border-primary pt-3 flex items-baseline justify-between">
                        <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Total</span>
                        <span className="text-xl font-heading font-black text-primary tabular-nums" data-testid="preview-total">
                            {preview.grand.toFixed(2)} €
                        </span>
                    </div>
                </aside>
            </div>
        </div>
    );
}

// ---------- Category section ----------
function CategorySection({ category, entries, onAdd, onChange, onRemove, globalWarrantyMonths, editing }) {
    const [open, setOpen] = useState(entries.length > 0);
    useEffect(() => {
        if (entries.length > 0) setOpen(true);
    }, [entries.length]);
    return (
        <div className="rounded-2xl border border-border bg-white overflow-hidden" data-testid={`cat-section-${category.value}`}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-muted/50"
            >
                <div className="flex items-center gap-3">
                    <span className="inline-flex h-8 min-w-[2rem] px-2 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-bold">
                        {entries.length}
                    </span>
                    <div>
                        <div className="font-heading font-black text-primary">{category.label}</div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">{category.value}</div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button type="button" onClick={(e) => { e.stopPropagation(); onAdd(); setOpen(true); }}
                        data-testid={`btn-add-part-${category.value}`}
                        className="inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-3 py-1 text-xs font-bold hover:-translate-y-0.5 transition">
                        <Plus className="h-3 w-3" /> Peça
                    </button>
                    {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                </div>
            </button>
            {open && (
                <div className="px-4 pb-4 space-y-3">
                    {entries.length === 0 && (
                        <div className="text-xs text-muted-foreground italic py-2">Sem peças nesta categoria.</div>
                    )}
                    {entries.map(({ p, i }) => (
                        <PartRow
                            key={p.id || `new-${i}`}
                            part={p}
                            onChange={(patch) => onChange(i, patch)}
                            onRemove={() => onRemove(i)}
                            globalWarrantyMonths={globalWarrantyMonths}
                            editing={editing}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

// ---------- Part row ----------
function PartRow({ part, onChange, onRemove, globalWarrantyMonths, editing }) {
    const inheritsGlobal = part.warranty_months === null || part.warranty_months === "" || part.warranty_months === undefined;
    const canPrintLabel = editing !== "new" && !!part.id;

    return (
        <div className="rounded-xl border border-border bg-muted/30 p-3" data-testid={`part-row-${part.id || part.name}`}>
            <div className="grid gap-2 sm:grid-cols-12 items-start">
                <div className="sm:col-span-4">
                    <label className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground">Nome *</label>
                    <input value={part.name} onChange={(e) => onChange({ name: e.target.value })}
                        placeholder="Ex: Estrutura em inox 304"
                        data-testid="part-name"
                        className="mt-1 w-full rounded-md border border-border bg-white px-2 py-1.5 text-sm outline-none focus:border-primary" />
                </div>
                <div className="sm:col-span-2">
                    <label className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground">SKU</label>
                    <input value={part.sku || ""} onChange={(e) => onChange({ sku: e.target.value })}
                        className="mt-1 w-full rounded-md border border-border bg-white px-2 py-1.5 text-xs font-mono outline-none focus:border-primary" />
                </div>
                <div className="sm:col-span-1">
                    <label className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground">Qtd</label>
                    <input type="number" min={1} value={part.quantity ?? 1} onChange={(e) => onChange({ quantity: e.target.value })}
                        className="mt-1 w-full rounded-md border border-border bg-white px-2 py-1.5 text-sm outline-none focus:border-primary" />
                </div>
                <div className="sm:col-span-2">
                    <label className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground">Preço un. €</label>
                    <input type="number" step="0.01" min={0} value={part.unit_price_eur ?? ""}
                        onChange={(e) => onChange({ unit_price_eur: e.target.value })}
                        data-testid="part-price"
                        className="mt-1 w-full rounded-md border border-border bg-white px-2 py-1.5 text-sm tabular-nums outline-none focus:border-primary" />
                </div>
                <div className="sm:col-span-2">
                    <label className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground">Garantia (m)</label>
                    <input type="number" min={0} max={240}
                        placeholder={`global (${globalWarrantyMonths || 24})`}
                        value={inheritsGlobal ? "" : part.warranty_months}
                        onChange={(e) => onChange({ warranty_months: e.target.value === "" ? null : e.target.value })}
                        data-testid="part-warranty"
                        className="mt-1 w-full rounded-md border border-border bg-white px-2 py-1.5 text-sm outline-none focus:border-primary" />
                </div>
                <div className="sm:col-span-1 flex justify-end pt-4">
                    <button type="button" onClick={onRemove}
                        data-testid="part-remove"
                        className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700 hover:bg-red-100">
                        <Trash2 className="h-3 w-3" />
                    </button>
                </div>
            </div>

            <div className="mt-2 grid gap-2 sm:grid-cols-12">
                <div className="sm:col-span-3">
                    <label className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground">Fornecedor</label>
                    <input value={part.supplier || ""} onChange={(e) => onChange({ supplier: e.target.value })}
                        className="mt-1 w-full rounded-md border border-border bg-white px-2 py-1.5 text-xs outline-none focus:border-primary" />
                </div>
                <div className="sm:col-span-2">
                    <label className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground">Instalada em</label>
                    <input type="date" value={part.installed_at || ""} onChange={(e) => onChange({ installed_at: e.target.value })}
                        className="mt-1 w-full rounded-md border border-border bg-white px-2 py-1.5 text-xs outline-none focus:border-primary" />
                </div>
                <div className="sm:col-span-5">
                    <label className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground">Notas</label>
                    <input value={part.notes || ""} onChange={(e) => onChange({ notes: e.target.value })}
                        className="mt-1 w-full rounded-md border border-border bg-white px-2 py-1.5 text-xs outline-none focus:border-primary" />
                </div>
                <div className="sm:col-span-2 flex flex-col justify-end gap-1">
                    {part.id && (
                        <div className="text-[10px] font-mono text-muted-foreground flex items-center gap-1">
                            <Tag className="h-3 w-3" /> {part.id}
                        </div>
                    )}
                    {canPrintLabel ? (
                        <Link to={`/admin/parts/${part.id}/label?cart=${editing}`} target="_blank" rel="noreferrer"
                            data-testid={`part-label-${part.id}`}
                            className="inline-flex items-center gap-1 rounded-full border border-border bg-white px-2 py-1 text-[10px] font-bold text-primary hover:bg-muted">
                            <ScanLine className="h-3 w-3" /> Etiqueta
                        </Link>
                    ) : (
                        <span className="text-[10px] italic text-muted-foreground">Guarde para gerar código</span>
                    )}
                </div>
            </div>

            {part.warranty_until && (
                <div className="mt-2 flex items-center gap-2 text-[10px] text-muted-foreground">
                    <ShieldCheck className="h-3 w-3" /> Garantia até {part.warranty_until}
                    {inheritsGlobal && <span className="italic">(herda global)</span>}
                </div>
            )}
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">{label}{required && " *"}</span>
            <div className="mt-2">{children}</div>
        </label>
    );
}
